import { throwError, Observable } from "rxjs";
import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from "@angular/common/http";
import { Vehicle } from "../models/vehicle";
import { PageConfiguration } from "../models/page-configuration";
import { HotelPageConfiguration } from "../models/hotel-page-configuration";
import { LocalString } from "../models/local-string";
import { map, flatMap, first, catchError, shareReplay } from "rxjs/operators";

import { environment } from "../../environments/environment";
import { parseString } from "xml2js";

@Injectable()
export class VolvoApiService {
  private apiBaseUrl = `https://${
    environment.staging ? "staging." : environment.production ? "" : "dev."
  }aplanbyvolvo.com/AV/API/`;
  private vehicleService: string = this.apiBaseUrl + "Vehicle.asmx/";
  private promotionService: string = this.apiBaseUrl + "Promotion.asmx/";
  private programService: string = this.apiBaseUrl + "Program.asmx/";
  private hotelPromotionService: string =
    this.apiBaseUrl + "HotelPromotion.asmx/";
  private nearestDealerService: string =
    this.apiBaseUrl + "NearestDealer.asmx/";

  private httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
    }),
    params: null,
  };

  private vehicles$: Observable<any[]>;
  private vehicle$: Observable<Vehicle>;
  private localStrings$: Observable<any[]>;
  private pageConfig$: Observable<PageConfiguration>;
  private partners$: Observable<any>;
  private promotionPartners$: Observable<any>;
  private states$: Observable<any>;
  private addFields$: Observable<any>;
  private pricingTable$: Observable<any>;
  private idMeData$: Observable<any>;
  private currentVehicle$: Observable<any>;

  constructor(private http: HttpClient) { }

  /**
   * Get the current domain based in the browser address
   * TODO: This needs to be revisted if this app is recompiled to mobile APP
   */
  getDomain(): string {
    let domain = window.location.hostname;

    // Check the domain, and if its one of the tests domains, falls back to volvoaffinity.com
      if (environment.local) {
      domain = "localhost:4200";
    } else if (!environment.production && !environment.staging) {
      domain = "dev.volvoaffinity.com";
      }

    return domain;
  }

  getCultureCode(): string {
    return "en-US";
  }

  /**
   * Get a list of vehicles organized in categories based on promotion alias
   * @param Domain
   * @param PromotionAlias
   */
  getVehicles(PromotionAlias: string): Observable<any[]> {
    if (!this.vehicles$) {
      const url = this.vehicleService + 'GetVehicles';
      const body = { Domain: this.getDomain(), PromotionAlias: PromotionAlias };
      this.vehicles$ = this.http
        .post(url, body, this.httpOptions)
        .pipe(
          map(r => r['d']),
          shareReplay(),
          catchError(e => this.handleError(e, body))
        );
    }
    return this.vehicles$;
  }

  /**
   * Get vehicle with info, images and billboard content,
   * Get data from vehicles$ when exists
   * or retrieve by id from server when not
   * @param domain
   * @param promotionAlias
   * @param vehicleId
   */
  getVehicle(promotionAlias: string, vehicleId: number | string): Observable<Vehicle> {
    if (this.vehicles$) {
      this.vehicle$ = this.vehicles$.pipe(
        map(res => {
          const vlist = [];
          res.forEach(cat => {
            Array.prototype.push.apply(vlist, cat.Vehicles);
          });
          return vlist;
        }),
        flatMap(vehicles => vehicles),
        first(vehicle => vehicle.ID.toString() === vehicleId.toString()),
        catchError(e => this.handleError(e, null))
      );
    } else {
      const url = this.vehicleService + 'GetVehicle';
      const body = { Domain: this.getDomain(), PromotionAlias: promotionAlias, vehicleID: vehicleId };
      this.vehicle$ = this.http
        .post(url, body, this.httpOptions)
        .pipe(
          map(r => Object.assign(new Vehicle, r['d'])),
          catchError(e => this.handleError(e, body))
        );
    }
    return this.vehicle$;
  }

  /**
   * Get landing page information
   * @param Domain
   * @param PromotionAlias
   */
  getPageConfiguration(PromotionAlias: string): Observable<PageConfiguration> {
    if (!this.pageConfig$) {
      const url = this.promotionService + 'GetPromotion';
      const body = {
        Domain: this.getDomain(),
        PromotionAlias: PromotionAlias,
        culture: this.getCultureCode() };
      this.pageConfig$ = this.http
        .post(url, body, this.httpOptions)
        .pipe(
          map(r => Object.assign(new PageConfiguration, r['d'])),
          shareReplay(),
          catchError(e => this.handleError(e, body))

        );
    }
    return this.pageConfig$;
  }

  /**
  * Get landing page information
  * @param PromotionAlias
  */
 getHotelPageConfiguration(PromotionAlias: string): Observable<PageConfiguration> {
   if (!this.pageConfig$) {
     const url = this.hotelPromotionService + 'GetHotelPromotion';
     const body = {
       Domain: this.getDomain() + '/hotel',
       PromotionAlias: PromotionAlias,
       culture: this.getCultureCode()
     };
     this.pageConfig$ = this.http
       .post(url, body, this.httpOptions)
       .pipe(
         map(r => Object.assign(new HotelPageConfiguration, r['d'])),
         shareReplay(),
         catchError(e => this.handleError(e, body))
       );
   }
   return this.pageConfig$;
 }

 /**
 * Get landing page information
 * @param AccessCode
 */
  getUser(AccessCode: string): Observable<any> {
    const url = this.hotelPromotionService + 'GetUserIdentification';
    const body = { AccessCode: AccessCode };
    const id = this.http
      .post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  return id;
}

 getPromotionLocalizationString(): Observable<any> {
   if (!this.localStrings$) {
     const url = this.promotionService + 'GetPromotionLocalizationString';
     const body = { cultureCode: this.getCultureCode() };

     return this.http.post(url, body, this.httpOptions)
       .pipe(
         map(res => {
           const obj = new Object;
           res['d'].map(element => {
             const localStringObj = Object.assign(new LocalString, element);
             obj[localStringObj.Key] = localStringObj.Value;
           });
           return obj;
         }),
         shareReplay(),
         catchError(e => this.handleError(e, body))
       );
   }
   return this.localStrings$;
 }

 getAvailablePartners(PromotionGuid: string): Observable<any> {
   if (!this.partners$) {
     const url = this.promotionService + 'GetAvailablePartners';
     const body = {
       PromotionGuid: PromotionGuid,
       culture: this.getCultureCode()

      };
     return this.http.post(url, body, this.httpOptions)
       .pipe(
         map(r => r['d']),
         shareReplay(),
         catchError(e => this.handleError(e, body))
       );
   }
   return this.partners$;
 }


 getAvailablePartnersSponsor(PromotionGuid: string): Observable<any> {
  if (!this.partners$) {
    const url = this.promotionService + 'GetAvailablePartnersSponsor';
    const body = {
      PromotionGuid: PromotionGuid,
      culture: this.getCultureCode()

     };
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }
  return this.partners$;
}

 getAvailableHotelPartners(PromotionGuid: string): Observable<any> {
   if (!this.partners$) {
     const url = this.hotelPromotionService + 'GetAvailableHotelPartners';
     const body = {
      PromotionGuid: PromotionGuid,
      culture: this.getCultureCode()
    };
     return this.http.post(url, body, this.httpOptions)
       .pipe(
         map(r => r['d']),
         shareReplay(),
         catchError(e => this.handleError(e, body))
       );
   }
   return this.partners$;
 }

  getPromotionPartners(PromotionGuid: string): Observable<any> {
    if (!this.promotionPartners$) {
      const url = this.promotionService + 'GetPromotionPartners';
      const body = {
        PromotionGuid: PromotionGuid,
        culture: this.getCultureCode()
       };
      return this.http.post(url, body, this.httpOptions)
        .pipe(
          map(r => r['d']),
          shareReplay(),
          catchError(e => this.handleError(e, body))
        );
    }
    return this.promotionPartners$;
  }

  getStates(CountryID: number): Observable<any> {
    if (!this.states$) {
      const url = this.programService + 'AvailableStates';
      const body = { CountryId: CountryID };
      return this.http.post(url, body, this.httpOptions)
        .pipe(
          map(r => r['d']),
          shareReplay(),
          catchError(e => this.handleError(e, body))
        );
    }
    return this.states$;
  }

  getCurrentVehicle(): Observable<any> {
    if (!this.currentVehicle$) {
      const url = this.hotelPromotionService + 'GetHotelPromotionVehicle';
      const body = { cultureCode: this.getCultureCode() };
      return this.http.post(url, body, this.httpOptions)
        .pipe(
          map(r => r['d']),
          shareReplay(),
          catchError(e => this.handleError(e, body))
        );
    }
    return this.currentVehicle$;
  }

  getAdditionalFields(culture: string, PromotionGuid: string): Observable<any> {
    if (!this.addFields$) {
      const url = this.promotionService + 'LoadExtraFields';
      const body = { culture: culture, PromotionGuid: PromotionGuid };
      return this.http.post(url, body, this.httpOptions)
        .pipe(
          map(r => this.XMLObjectFromString(r['d'])),
          shareReplay(),
          catchError(e => this.handleError(e, body))
        );
    }
    return this.addFields$;
  }

  getPricingTable(promotionAlias: string, vehicleId: string): Observable<any> {
    if (!this.pricingTable$) {
      const url = this.vehicleService + 'GetPricingTable';
      const body = {
        Domain: this.getDomain(),
        PromotionAlias: promotionAlias,
        vehicleID: vehicleId
      };
      return this.http.post(url, body, this.httpOptions)
        .pipe(
          map(r => r['d']),
          shareReplay(),
          catchError(e => this.handleError(e, body))
        );
    }
    return this.pricingTable$;
  }

  
  getNearestDealers(_address:string, _city: string, _zip: string, _countryID:number): Observable<any> {
    const url = this.nearestDealerService + 'GetGeolocationNearestDealersByPinAddress';
    const body = {
      address: _address,
      city: _city,
      zip: _zip,
      country: 'US',
      origin: 'AFFINITY'
    };


    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
      
  };

  createPin(data: Object): Observable<any> {
    const url = this.promotionService + 'CreatePin';
    const body = data;
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }

  CreateSponsorPin(data: Object): Observable<any> {
    const url = this.promotionService + 'CreateSponsorPin';
    const body = data;
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }


  CreateFleetPin(data: Object): Observable<any> {
    const url = this.promotionService + 'CreateFleetPin';
    const body = data;
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }

  CreateFleetSponsorPin(data: Object): Observable<any> {
    const url = this.promotionService + 'CreateFleetSponsorPin';
    const body = data;
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }

  CreateRedSoxPin(data: Object): Observable<any> {
    const url = this.promotionService + 'CreateRedSoxPin';
    const body = data;
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }

  CreateRedSoxSponsorPin(data: Object): Observable<any> {
    const url = this.promotionService + 'CreateRedSoxSponsorPin';
    const body = data;
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }  

  createHotelPin(data: Object): Observable<any> {
    const url = this.hotelPromotionService + 'CreatePin';
    const body = data;
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }

  /**
   * save kentico form using named web service
   * @param jsonData e.g.{ "name": "", "fromAddress": "", "toAddress": "", "message": "", "referringPtoUrl" : "" }
   * @param serviceName
   */
  saveBizForm(jsonData: string, serviceName: string): Observable<any> {
    const url = this.promotionService + serviceName;
    const body = {
      formData: jsonData
    };
    return this.http.post(url, body, this.httpOptions)
      .pipe(
        map(r => r['d']),
        shareReplay(),
        catchError(e => this.handleError(e, body))
      );
  }

  getIdMeUserData(promotionAlias: string, idMeCode: string): Observable<any> {
    if (!this.idMeData$) {
      const url = this.promotionService + 'GetUserDataFromIdMe';
      const body = {
        Domain: this.getDomain(),
        PromotionAlias: promotionAlias,
        idMeCode: idMeCode
      };
      return this.http.post(url, body, this.httpOptions)
        .pipe(
          map(r => r['d']),
          shareReplay(),
          catchError(e => this.handleError(e, body))
        );
    }
    return this.idMeData$;
  }

  private MapArrayFromResult(result: Object, index: string): Array<Object> {
    return JSON.parse('{' + result['d'] + '}')[index];
  }

  XMLObjectFromString(result: Object): any {

    const parseOptions = {
      mergeAttrs: true
    };
    let xml = null;
    parseString(result, parseOptions, function (err, xmlresult) {
      xml = xmlresult;
    });
    return xml;
  }

  private handleError(errorResponse: HttpErrorResponse, requestBody: object) {
    let errorMsg: string;
    
    if (errorResponse.error instanceof Error) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMsg = 'An error occurred:' + errorResponse.error.message;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      errorMsg = `Backend returned code ${errorResponse.status}, body was: ${errorResponse.error}`;
    }
    
    if(requestBody) {
      console.log('The HTTP Request body was:', requestBody);
    }
    
    console.log(errorMsg);
    console.log(errorResponse);
    
    if(errorResponse.error)
      console.log(errorResponse.error);

    if(errorResponse.error.error)
      console.log(errorResponse.error.error);

    return throwError(errorResponse);
  }

}
